import useInboxStore from '@/stores/InboxStore';
import { Handle, Position } from '@xyflow/react';
import {
  AlertCircle,
  Archive,
  Bot,
  Check,
  Clock,
  Linkedin,
  MessageCircle,
  MessageSquare,
  Tag,
  Trash2,
  Users,
  Webhook,
  Workflow,
} from 'lucide-react';
import { v4 as uuidv4 } from 'uuid';
import { useShallow } from 'zustand/react/shallow';

import { cn } from '@/lib/utils';

// // Action Node - Represents a action in the workflow
export const ActionNode = ({ data, selected, id }) => {
  const isAction = data.actionType !== undefined;

  // Get the appropriate icon based on action type
  const getIcon = () => {
    if (isAction) {
      switch (data.actionType) {
        case 'send_message':
          return MessageSquare;
        case 'reply_comment':
          return MessageCircle;
        case 'contact_add_tag':
        case 'contact_remove_tag':
          return Tag;
        case 'contact_state_change':
          return Archive;
        case 'contact_change_owner':
          return Users;
        case 'delay':
          return Clock;
        case 'branch':
          return Workflow;
        case 'trigger_webhook':
          return Webhook;
        case 'ai_select_message':
          return Bot;
        case 'linkedin_reply_comment':
          return Linkedin;
        default:
          return data.icon || Workflow;
      }
    }
    return data.icon || Workflow;
  };

  const Icon = getIcon();

  return (
    <div
      className={cn(
        'relative flex min-h-[120px] w-[380px] flex-col items-center justify-center gap-2 rounded-lg border bg-background p-4 shadow-sm',
        data.bgClass || 'bg-secondary/20',
        selected && 'ring-2 ring-primary'
      )}
      //   data-id={id}
      //   onClick={(e) => {
      //     e.stopPropagation();
      //     if (isAction && data.onClick) {
      //       data.onClick(id);
      //     }
      //   }}
    >
      {/* Node Header */}
      <div className='flex w-full items-center justify-between'>
        <div className='flex items-center gap-2'>
          <div
            className={`flex h-8 w-8 items-center justify-center rounded-md ${
              data.bgClass || 'bg-secondary/20'
            }`}
          >
            {Icon && (
              <Icon
                className={`h-5 w-5 ${
                  data.iconClass || 'text-secondary-foreground'
                }`}
              />
            )}
          </div>
          <p className='text-sm font-medium'>{data.label || 'Task'}</p>
        </div>
        {data.handleActionDelete && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              data.handleActionDelete(id);
            }}
            className='rounded-full p-1 hover:bg-slate-100'
          >
            <Trash2 size={16} className='text-slate-500' />
          </button>
        )}
      </div>

      {/* Node Description */}
      {data.description && (
        <p className='text-xs text-muted-foreground'>{data.description}</p>
      )}

      {/* Action Configuration Display */}
      {isAction && data.actionType && (
        <div className='mt-2 w-full space-y-2'>
          {/* Webhook Configuration */}
          {data.actionType === 'trigger_webhook' && data.config?.url && (
            <div className='rounded-md border bg-secondary/20 p-2 text-xs'>
              <div className='flex items-center gap-1'>
                <span className='truncate'>
                  {data.config.method} {data.config.url}
                </span>
              </div>
            </div>
          )}

          {/* Send Message Configuration */}
          {data.actionType === 'send_message' && data.config?.message && (
            <div className='rounded-md border bg-primary/10 p-2 text-xs'>
              <div className='mb-1 font-medium'>Message:</div>
              <div className='whitespace-pre-wrap'>{data.config.message}</div>
            </div>
          )}

          {/* Reply Comment Configuration */}
          {data.actionType === 'reply_comment' && data.config?.comment && (
            <div className='rounded-md border bg-primary/10 p-2 text-xs'>
              <div className='mb-1 font-medium'>Reply:</div>
              <div className='whitespace-pre-wrap'>{data.config.comment}</div>
            </div>
          )}

          {/* Add Tag Configuration */}
          {data.actionType === 'contact_add_tag' && data.config?.tagObjects && (
            <div className='rounded-md border bg-secondary/20 p-2 text-xs'>
              <div className='mb-1 font-medium'>Add Tags:</div>
              <div className='mt-1 flex flex-wrap gap-1'>
                {data.config.tagObjects.map((tag, index) => (
                  <span
                    key={index}
                    className='inline-flex items-center rounded bg-secondary/30 px-1.5 py-0.5'
                  >
                    {tag.colour && (
                      <span
                        className='mr-1 inline-block h-2 w-2 rounded-full'
                        style={{ backgroundColor: tag.colour }}
                      />
                    )}
                    {tag.name}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Remove Tag Configuration */}
          {data.actionType === 'contact_remove_tag' &&
            data.config?.tagObjects && (
              <div className='rounded-md border bg-destructive/10 p-2 text-xs'>
                <div className='mb-1 font-medium'>Remove Tags:</div>
                <div className='mt-1 flex flex-wrap gap-1'>
                  {data.config.tagObjects.map((tag, index) => (
                    <span
                      key={index}
                      className='inline-flex items-center rounded bg-destructive/20 px-1.5 py-0.5'
                    >
                      {tag.colour && (
                        <span
                          className='mr-1 inline-block h-2 w-2 rounded-full'
                          style={{ backgroundColor: tag.colour }}
                        />
                      )}
                      {tag.name}
                    </span>
                  ))}
                </div>
              </div>
            )}

          {/* Change State Configuration */}
          {data.actionType === 'contact_state_change' && data.config?.state && (
            <div className='rounded-md border bg-primary/10 p-2 text-xs'>
              <div className='mb-1 font-medium'>Set State:</div>
              <div className='capitalize'>{data.config.state}</div>
            </div>
          )}

          {/* Change Owner Configuration - Single Owner */}
          {data.actionType === 'contact_change_owner' &&
            data.config?.ownerMode !== 'multiple' &&
            data.config?.ownerObject && (
              <div className='rounded-md border bg-secondary/20 p-2 text-xs'>
                <div className='mb-1 font-medium'>Set Owner:</div>
                <div>{data.config.ownerObject.name}</div>
              </div>
            )}

          {/* Change Owner Configuration - Multiple Owners */}
          {data.actionType === 'contact_change_owner' &&
            data.config?.ownerMode === 'multiple' &&
            data.config?.ownersWithDetails?.length > 0 && (
              <div className='rounded-md border bg-secondary/20 p-2 text-xs'>
                <div className='mb-1 font-medium'>Weighted Assignment:</div>
                <div className='space-y-1'>
                  {data.config.ownersWithDetails.map((alloc, idx) => (
                    <div
                      key={idx}
                      className='flex items-center justify-between'
                    >
                      <span>{alloc.ownerObject?.name || 'Unknown'}</span>
                      <span className='font-medium'>{alloc.allocation}%</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

          {/* Delay Action Configuration */}
          {data.actionType === 'delay' && data.config?.value && (
            <div className='rounded-md border bg-primary/10 p-2 text-xs'>
              <div className='mb-1 font-medium'>Wait for:</div>
              <div className='flex items-center gap-1'>
                <Clock className='h-3 w-3 text-muted-foreground' />
                <span>
                  {data.config.value} {data.config.unit || 'minutes'}{' '}
                </span>
              </div>
            </div>
          )}

          {/* Branch Configuration */}
          {data.actionType === 'branch' && (
            <div className='rounded-md border bg-secondary/20 p-2 text-xs'>
              <div className='mb-1 font-medium'>
                Branch: {data.config?.name || 'Unnamed Branch'}
              </div>
              {data.config?.conditions && data.config.conditions.length > 0 ? (
                <div className='space-y-1 text-muted-foreground'>
                  {data.config.conditions.map((group, groupIndex) => (
                    <div key={groupIndex} className='pb-1'>
                      {groupIndex > 0 && (
                        <div className='mb-1 mt-1 border-t pt-1 text-xs font-medium text-secondary-foreground'>
                          OR
                        </div>
                      )}
                      {group.map((condition, condIndex) => {
                        // Format condition based on field type
                        let displayValue = '';
                        let displayOperator = '';

                        // Get operator display text
                        switch (condition.operator) {
                          case 'is_one_of':
                            displayOperator = 'is one of';
                            break;
                          case 'equals':
                            displayOperator = '=';
                            break;
                          case 'is':
                            displayOperator = 'is';
                            break;
                          case 'gt':
                            displayOperator = '>';
                            break;
                          case 'lt':
                            displayOperator = '<';
                            break;
                          case 'within':
                            displayOperator = 'within';
                            break;
                          default:
                            displayOperator = condition.operator;
                        }

                        // Get value display text based on field
                        if (
                          condition.field === 'user_id' &&
                          Array.isArray(condition.value)
                        ) {
                          if (
                            condition.operator === 'equals' &&
                            condition.value.length === 1
                          ) {
                            displayValue = 'single owner';
                          } else {
                            displayValue = `${condition.value.length} owners`;
                          }
                        } else if (
                          condition.field === 'tag_ids' &&
                          Array.isArray(condition.value)
                        ) {
                          if (
                            condition.operator === 'equals' &&
                            condition.value.length === 1
                          ) {
                            displayValue = 'single tag';
                          } else {
                            displayValue = `${condition.value.length} tags`;
                          }
                        } else if (condition.field === 'status') {
                          displayValue = condition.value || '';
                        } else if (condition.field === 'verified') {
                          displayValue =
                            condition.value === 'true' ? 'true' : 'false';
                        } else if (
                          condition.field === '_is_msgs_received_within'
                        ) {
                          displayValue = `${condition.value} hours`;
                        } else {
                          displayValue = condition.value || '';
                        }

                        // Get field display name
                        let fieldName = '';
                        switch (condition.field) {
                          case 'user_id':
                            fieldName = 'Owner';
                            break;
                          case 'status':
                            fieldName = 'Status';
                            break;
                          case 'tag_ids':
                            fieldName = 'Tags';
                            break;
                          case 'verified':
                            fieldName = 'IsVerified';
                            break;
                          case 'followers_count':
                            fieldName = 'Followers';
                            break;
                          case 'following_count':
                            fieldName = 'Following';
                            break;
                          case '_is_msgs_received_within':
                            fieldName = 'Message received';
                            break;
                          default:
                            fieldName = condition.field;
                        }

                        return (
                          <div
                            key={condIndex}
                            className='flex items-center gap-1 text-xs'
                          >
                            {condIndex > 0 && (
                              <span className='ml-1 mr-1 text-xs'>AND</span>
                            )}
                            <span className='font-medium'>{fieldName}</span>
                            <span>{displayOperator}</span>
                            <span className='rounded bg-secondary/30 px-1'>
                              {displayValue}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  ))}
                </div>
              ) : (
                <div className='italic text-muted-foreground'>
                  No conditions configured
                </div>
              )}
            </div>
          )}

          {/* AI Select Message Configuration */}
          {data.actionType === 'ai_select_message' && data.config?.messages && (
            <div className='rounded-md border bg-purple-500/10 p-2 text-xs'>
              {data.config.custom_prompt && (
                <div className='mb-2 text-xs'>
                  <div className='font-medium'>Custom Prompt:</div>
                  <div
                    className='overflow-hidden italic text-muted-foreground'
                    style={{
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      lineHeight: '1.2em',
                      maxHeight: '2.4em',
                    }}
                  >
                    {data.config.custom_prompt}
                  </div>
                </div>
              )}
              <div className='font-medium'>Message Options:</div>
              <div className='space-y-1'>
                <div className='rounded bg-purple-500/20 px-2 py-1 text-center text-muted-foreground'>
                  {data.config.messages.length} message
                  {data.config.messages.length !== 1 ? 's' : ''}
                </div>
              </div>
            </div>
          )}

          {/* LinkedIn Comment Configuration */}
          {data.actionType === 'linkedin_reply_comment' && data.config?.comment && (
            <div className='rounded-md border bg-blue-600/10 p-2 text-xs'>
              <div className='mb-1 font-medium'>Comment Reply:</div>
              <div className='whitespace-pre-wrap'>{data.config.comment}</div>
            </div>
          )}

          {/* Delay Configuration - Keep for backward compatibility */}
          {data.delay && (
            <div className='mt-1 flex items-center gap-1 text-xs text-muted-foreground'>
              <Clock className='h-3 w-3' />
              <span>
                Delay: {data.delay.value} {data.delay.unit}
              </span>
            </div>
          )}

          {/* Conditions Summary */}
          {data.conditions && data.conditions.length > 0 && (
            <div className='mt-1 flex items-center gap-1 text-xs text-muted-foreground'>
              <AlertCircle className='h-3 w-3' />
              <span>
                {data.conditions.length} condition
                {data.conditions.length !== 1 ? 's' : ''}
              </span>
            </div>
          )}
        </div>
      )}

      {/* Node Handles */}
      <Handle
        type='target'
        position={Position.Top}
        className='!border-2 !border-white !bg-slate-400 hover:!bg-primary'
      />
      <Handle
        type='source'
        position={Position.Bottom}
        className='!border-2 !border-white !bg-slate-400 hover:!bg-primary'
      />
    </div>
  );
};

// // Branch Config Node - Represents the branching logic container
export const BranchConfigNode = ({ data, selected, id }) => {
  const Icon = data.icon || Workflow; // Use assigned icon or default

  return (
    <div
      className={cn(
        'relative flex min-h-[100px] w-[380px] flex-col items-center justify-center gap-2 rounded-lg border bg-background p-4 shadow-sm',
        data.bgClass || 'bg-secondary/20',
        selected && 'ring-2 ring-primary'
      )}
    >
      {/* Node Header */}
      <div className='flex w-full items-center justify-between'>
        <div className='flex items-center gap-2'>
          <div
            className={`flex h-8 w-8 items-center justify-center rounded-md ${
              data.bgClass || 'bg-secondary/20'
            }`}
          >
            {Icon && (
              <Icon
                className={`h-5 w-5 ${
                  data.iconClass || 'text-secondary-foreground'
                }`}
              />
            )}
          </div>
          {/* Use branch name from data if available, otherwise default */}
          <p className='text-sm font-medium'>{data.branchName || 'Branch'}</p>
        </div>
        {data.handleDeleteBranchConfig && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              data.handleDeleteBranchConfig(id);
            }}
            className='rounded-full p-1 hover:bg-slate-100'
          >
            <Trash2 size={16} className='text-slate-500' />
          </button>
        )}
      </div>

      {/* Display number of branches */}
      {Array.isArray(data.branches) && data.branches.length > 0 && (
        <p className='text-xs text-muted-foreground'>
          {data.branches.length} branch{data.branches.length !== 1 ? 'es' : ''}{' '}
          configured
        </p>
      )}

      {/* Node Handles */}
      <Handle
        type='target'
        position={Position.Top}
        className='!border-2 !border-white !bg-slate-400 hover:!bg-primary'
      />
      {/* Only show source handle if branches are configured */}
      {Array.isArray(data.branches) && data.branches.length > 0 && (
        <Handle
          type='source'
          position={Position.Bottom}
          id='branch-source' // Unique ID for the handle if needed
          className='!border-2 !border-white !bg-slate-400 hover:!bg-primary'
        />
      )}
    </div>
  );
};

// // Branch Data Node - Represents a single branch outcome path
export const BranchDataNode = ({ data, selected }) => {
  const { name = 'Branch Path', conditions = [], branchId } = data;
  const { owners, tags } = useInboxStore(
    useShallow((state) => ({
      owners: state.owners,
      tags: state.tags.filter(
        (tag) =>
          !['No-Tag-SBCCRM', 'Seen-Not-Responded', 'Not-Responded'].includes(
            tag._id
          )
      ),
    }))
  );

  // Helper to render condition summary
  const renderConditionSummary = (cond) => {
    let displayValue = '';
    let displayOperator = '';
    let fieldName = '';

    // Map field keys to user-friendly names
    const fieldNameMap = {
      'contact.user_id': 'Owner',
      'contact.status': 'Status',
      'contact.tag_ids': 'Tags',
      'contact.verified': 'Verified',
      'contact.followers_count': 'Followers',
      'contact.following_count': 'Following',
      'contact_messages._total_msgs_received': 'Total Msgs Received',
      'contact_messages._prev_message_received_timestamp': 'Prev Msg Received',
      'contact_messages._none_messages_received_within': 'No Msgs Since Start',
    };
    fieldName = fieldNameMap[cond.field] || cond.field; // Fallback to key if not mapped

    // Handle the special "No Messages" condition
    if (cond.field === 'contact_messages._none_messages_received_within') {
      return fieldName; // Just display the field name
    }

    // Map operators to user-friendly symbols/text
    switch (cond.operator) {
      case 'in':
        displayOperator = 'is one of';
        break;
      case 'equals':
        displayOperator = '=';
        break;
      case 'gt':
        displayOperator = '>';
        break;
      case 'lt':
        displayOperator = '<';
        break;
      default:
        displayOperator = cond.operator; // Fallback
    }

    // Format the value based on field and operator
    switch (cond.field) {
      case 'contact.user_id':
        if (cond.operator === 'equals') {
          const owner = owners.find((o) => o._id === cond.value);
          displayValue = owner ? `${owner.first_name} ${owner.last_name}` : '';
        } else if (cond.operator === 'in') {
          const selectedOwners = Array.isArray(cond.value)
            ? cond.value
                .map((id) => {
                  const owner = owners.find((o) => o._id === id);
                  return owner
                    ? `${owner.first_name} ${owner.last_name}`
                    : null;
                })
                .filter(Boolean)
            : [];
          displayValue = selectedOwners.join(', ');
        }
        break;
      case 'contact.tag_ids':
        if (cond.operator === 'equals') {
          const tag = tags.find((t) => t._id === cond.value);
          displayValue = tag ? tag.name : '';
        } else if (cond.operator === 'in') {
          const selectedTags = Array.isArray(cond.value)
            ? cond.value
                .map((id) => {
                  const tag = tags.find((t) => t._id === id);
                  return tag ? tag.name : null;
                })
                .filter(Boolean)
            : [];
          displayValue = selectedTags.join(', ');
        }
        break;
      case 'contact.status':
        displayValue = cond.value ? String(cond.value) : '';
        break;
      case 'contact.verified':
        displayValue = String(cond.value) === 'true' ? 'True' : 'False';
        break;
      case 'contact.followers_count':
      case 'contact.following_count':
      case 'contact_messages._total_msgs_received':
        displayValue = String(cond.value) || '';
        break;
      case 'contact_messages._prev_message_received_timestamp':
        // Special handling for the custom timestamp comparison
        if (
          cond.operator === 'compare_within_timestamp' &&
          typeof cond.value === 'object'
        ) {
          const { operator: compareOp, unit, unit_value } = cond.value;
          const operatorText =
            compareOp === 'gt' ? 'greater than' : 'less than';
          const unitText = unit || 'units'; // Fallback unit text
          const valueText = unit_value || '_';
          displayValue = `${valueText} ${unitText} ago`;
          displayOperator = operatorText; // Override operator display for this case
        } else {
          // Fallback for older simple comparison (assuming value is just days)
          displayValue = `${cond.value || '_'} days ago`;
          // Keep the default displayOperator ('<' or '>')
        }
        break;
      default:
        displayValue = String(cond.value) || ''; // Fallback
    }

    // For compare_within_timestamp, operator is handled within displayValue logic
    if (cond.operator === 'compare_within_timestamp') {
      return `${fieldName} ${displayOperator} ${displayValue}`.trim();
    } else {
      // Standard formatting for other operators
      return `${fieldName} ${displayOperator} ${displayValue}`.trim();
    }
  };

  return (
    <div
      className={cn(
        'relative flex min-h-[80px] w-[280px] flex-col items-start justify-center gap-2 rounded-lg border border-dashed border-slate-400 bg-background p-3 shadow-sm',
        selected && 'ring-2 ring-secondary'
      )}
      // This node is not directly interactive for configuration
    >
      {/* Branch Name */}
      <p className='text-sm font-medium'>{name}</p>

      {/* Condition Summary */}
      {conditions && conditions.length > 0 && conditions[0].length > 0 ? (
        <div className='space-y-1 text-xs text-muted-foreground'>
          {conditions.map((group, groupIndex) => (
            <div key={groupIndex} className='pb-1'>
              {groupIndex > 0 && (
                <span className='text-xs font-medium'>OR </span>
              )}
              {group.map((condition, condIndex) => (
                <span key={condIndex}>
                  {condIndex > 0 && (
                    <span className='ml-1 mr-1 text-xs'>AND </span>
                  )}
                  <span className='rounded bg-secondary/20 px-1'>
                    {renderConditionSummary(condition)}
                  </span>
                </span>
              ))}
            </div>
          ))}
        </div>
      ) : (
        name !== 'Fallback' && ( // Don't show for fallback unless it somehow gets conditions
          <p className='text-xs italic text-muted-foreground'>
            No conditions (Always runs)
          </p>
        )
      )}
      {name === 'Fallback' && (
        <p className='text-xs italic text-muted-foreground'>
          Runs if no other branch matches
        </p>
      )}

      {/* Node Handles */}
      <Handle
        type='target'
        position={Position.Top}
        id={`target-${branchId || uuidv4()}`} // Ensure unique handle ID per branch
        className='!border-2 !border-white !bg-slate-400 hover:!bg-primary'
      />
      <Handle
        type='source'
        position={Position.Bottom}
        id={`source-${branchId || uuidv4()}`} // Ensure unique handle ID per branch
        className='!border-2 !border-white !bg-slate-400 hover:!bg-primary'
      />
    </div>
  );
};

// Trigger Node - Represents the initial trigger for the workflow
export const TriggerNode = ({ data, selected }) => {
  return (
    <div
      className={cn(
        'relative flex min-h-[120px] w-[380px] flex-col items-center justify-center gap-2 rounded-lg border bg-background p-4 shadow-sm',
        data.bgClass || 'bg-primary/10',
        selected && 'ring-2 ring-primary'
      )}
    >
      {/* Node Header */}
      <div className='flex w-full items-center justify-center gap-2'>
        <div
          className={`flex h-8 w-8 items-center justify-center rounded-md ${
            data.bgClass || 'bg-primary/10'
          }`}
        >
          {data.icon ? (
            <data.icon
              className={`h-5 w-5 ${data.iconClass || 'text-primary'}`}
            />
          ) : (
            <Workflow
              className={`h-5 w-5 ${data.iconClass || 'text-primary'}`}
            />
          )}
        </div>
        <p className='text-sm font-medium'>
          {data.label || 'Select a trigger'}
        </p>
      </div>

      {/* Node Description */}
      {data.description && (
        <p className='text-xs text-muted-foreground'>{data.description}</p>
      )}

      {/* Trigger Configuration Display */}
      {data.config && (
        <div
          className={`mt-2 w-full rounded-md border p-2 text-xs ${
            data.borderClass || 'border-primary/20'
          } ${data.bgClass ? `${data.bgClass}` : 'bg-primary/10'}`}
        >
          {data.triggerType === 'message_text' && (
            <>
              <div className='text-xs font-medium'>Message Trigger:</div>
              <div className='text-xs'>
                {data.config.operator === 'equals' && (
                  <>Text equals &quot;{data.config.text}&quot;</>
                )}
                {data.config.operator === 'in' && data.config.texts && (
                  <>
                    <div>Text contains any of:</div>
                    <div className='mt-1 flex flex-wrap gap-1'>
                      {data.config.texts.map((text, index) => (
                        <span
                          key={index}
                          className='inline-block rounded-sm bg-primary/20 px-1.5 py-0.5 text-xs'
                        >
                          {text}
                        </span>
                      ))}
                    </div>
                  </>
                )}
                {data.config.operator === 'word_in' && data.config.words && (
                  <>
                    <div>Text contains any of the words within:</div>
                    <div className='mt-1 flex flex-wrap gap-1'>
                      {data.config.words.map((word, index) => (
                        <span
                          key={index}
                          className='inline-block rounded-sm bg-primary/20 px-1.5 py-0.5 text-xs'
                        >
                          {word}
                        </span>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </>
          )}
          {data.triggerType === 'comment_text' && (
            <>
              <div className='text-xs font-medium'>Comment Trigger:</div>
              <div className='text-xs'>
                {data.config.operator === 'equals' && (
                  <>Comment equals &quot;{data.config.text}&quot;</>
                )}
                {data.config.operator === 'in' && data.config.texts && (
                  <>
                    <div>Comment contains any of:</div>
                    <div className='mt-1 flex flex-wrap gap-1'>
                      {data.config.texts.map((text, index) => (
                        <span
                          key={index}
                          className='inline-block rounded-sm bg-primary/20 px-1.5 py-0.5 text-xs'
                        >
                          {text}
                        </span>
                      ))}
                    </div>
                  </>
                )}
                {data.config.operator === 'word_in' && data.config.words && (
                  <>
                    <div>Comment contains any of the words within:</div>
                    <div className='mt-1 flex flex-wrap gap-1'>
                      {data.config.words.map((word, index) => (
                        <span
                          key={index}
                          className='inline-block rounded-sm bg-primary/20 px-1.5 py-0.5 text-xs'
                        >
                          {word}
                        </span>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </>
          )}
          {data.triggerType === 'contact_tag' && (
            <>
              <div className='text-xs font-medium'>Tag Trigger:</div>
              <div className='text-xs'>
                {data.config.operator === 'equals' &&
                  data.config.tags &&
                  data.config.tags.length > 0 && (
                    <>
                      Tag equals &quot;
                      {data.config.tagObjects && data.config.tagObjects[0] ? (
                        <span className='inline-flex items-center'>
                          {data.config.tagObjects[0].colour && (
                            <span
                              className='mr-1 inline-block h-2 w-2 rounded-full'
                              style={{
                                backgroundColor:
                                  data.config.tagObjects[0].colour,
                              }}
                            />
                          )}
                          {data.config.tagObjects[0].name}
                        </span>
                      ) : (
                        data.config.tags[0]
                      )}
                      &quot;
                    </>
                  )}
                {data.config.operator === 'in' &&
                  data.config.tags &&
                  data.config.tags.length > 0 && (
                    <>
                      <div>Tag contains any of:</div>
                      <div className='mt-1 flex flex-wrap gap-1'>
                        {data.config.tagObjects
                          ? data.config.tagObjects.map((tagObj, index) => (
                              <span
                                key={index}
                                className='inline-flex items-center rounded-sm bg-secondary/20 px-1.5 py-0.5 text-xs'
                              >
                                {tagObj.colour && (
                                  <span
                                    className='mr-1 inline-block h-2 w-2 rounded-full'
                                    style={{ backgroundColor: tagObj.colour }}
                                  />
                                )}
                                {tagObj.name}
                              </span>
                            ))
                          : data.config.tags.map((tag, index) => (
                              <span
                                key={index}
                                className='inline-flex items-center rounded-sm bg-secondary/20 px-1.5 py-0.5 text-xs'
                              >
                                {tag}
                              </span>
                            ))}
                      </div>
                    </>
                  )}
              </div>
            </>
          )}
          {data.triggerType === 'schedule' && (
            <>
              <div className='text-xs font-medium'>Schedule Trigger:</div>
              <div className='text-xs'>
                <div>
                  Every {data.config.value?.value} {data.config.value?.unit}
                </div>
                {data.config.conditions &&
                  data.config.conditions.length > 0 && (
                    <div className='mt-1'>
                      <div>With conditions:</div>
                      <div className='mt-1 space-y-1'>
                        {data.config.conditions.map((condition, index) => (
                          <div
                            key={index}
                            className='rounded-sm bg-orange-500/20 px-1.5 py-0.5'
                          >
                            {condition.field === 'contact_tag' && (
                              <>
                                {condition.operator === 'equals'
                                  ? 'Tag equals'
                                  : 'Tag contains any of'}
                                :{' '}
                                {condition.operator === 'equals' ? (
                                  <span className='inline-flex items-center'>
                                    {condition.tagObjects[0].colour && (
                                      <span
                                        className='mr-1 inline-block h-2 w-2 rounded-full'
                                        style={{
                                          backgroundColor:
                                            condition.tagObjects[0].colour,
                                        }}
                                      />
                                    )}
                                    {condition.tagObjects[0].name}
                                  </span>
                                ) : (
                                  <div className='mt-1 flex flex-wrap gap-1'>
                                    {condition.tagObjects?.map((id, index) => {
                                      return (
                                        <span
                                          key={index}
                                          className='inline-flex items-center rounded-sm bg-secondary/20 px-1.5 py-0.5'
                                        >
                                          {id.colour && (
                                            <span
                                              className='mr-1 inline-block h-2 w-2 rounded-full'
                                              style={{
                                                backgroundColor: id.colour,
                                              }}
                                            />
                                          )}
                                          {id.name}
                                        </span>
                                      );
                                    })}
                                  </div>
                                )}
                              </>
                            )}
                            {condition.field ===
                              'contact.latest_message_received_timestamp' &&
                              `Time since last message from contact > ${condition.value.unit_value} ${condition.value.unit}`}
                            {condition.field ===
                              'contact.latest_message_sent_timestamp' &&
                              `Time since last message sent to contact > ${condition.value.unit_value} ${condition.value.unit}`}
                            {condition.field ===
                              'contact.latest_message_direction' &&
                              `Last message direction: ${condition.value}`}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
              </div>
            </>
          )}
          {data.triggerType === 'any_message' && (
            <>
              <div className='text-xs font-medium'>Any Message Trigger:</div>
              <div className='text-xs'>
                Triggers whenever any message is received
              </div>
            </>
          )}
          {data.triggerType === 'linkedin_post_comment' && (
            <>
              <div className='text-xs'>
                {data.config.postDetails &&
                Array.isArray(data.config.postDetails) ? (
                  <div>
                    <span>Monitors: </span>
                    <span className='font-medium'>
                      {data.config.postDetails.length} post
                      {data.config.postDetails.length !== 1 ? 's' : ''}
                    </span>
                  </div>
                ) : data.config.postDetails?.title ? (
                  <div>
                    <span>Monitors: </span>
                    <span className='font-medium'>
                      {data.config.postDetails.title}
                    </span>
                  </div>
                ) : data.config.post_ids?.length > 0 ? (
                  <div>
                    <span>Monitors: </span>
                    <span className='font-medium'>
                      {data.config.post_ids.length} post
                      {data.config.post_ids.length !== 1 ? 's' : ''}
                    </span>
                  </div>
                ) : (
                  <div>Select posts to monitor</div>
                )}
                {data.config.operator === 'equals' && (
                  <div className='mt-1'>
                    Comment equals &quot;{data.config.text}&quot;
                  </div>
                )}
                {data.config.operator === 'in' && data.config.texts && (
                  <>
                    <div className='mt-1'>Comment contains any of:</div>
                    <div className='mt-1 flex flex-wrap gap-1'>
                      {data.config.texts.map((text, index) => (
                        <span
                          key={index}
                          className='inline-block rounded-sm bg-blue-600/20 px-1.5 py-0.5 text-xs'
                        >
                          {text}
                        </span>
                      ))}
                    </div>
                  </>
                )}
                {data.config.operator === 'word_in' && data.config.words && (
                  <>
                    <div className='mt-1'>
                      Comment contains any of the words:
                    </div>
                    <div className='mt-1 flex flex-wrap gap-1'>
                      {data.config.words.map((word, index) => (
                        <span
                          key={index}
                          className='inline-block rounded-sm bg-blue-600/20 px-1.5 py-0.5 text-xs'
                        >
                          {word}
                        </span>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </>
          )}
        </div>
      )}

      {/* Only show the handle when a valid trigger configuration exists */}
      {/* Node Handle */}
      {data.config && (
        <Handle
          type='source'
          position={Position.Bottom}
          className='!border-2 !border-white !bg-slate-400 hover:!bg-primary'
        />
      )}
    </div>
  );
};

// End Node - Represents the end of the workflow
export const EndNode = ({ data, selected }) => {
  return (
    <div
      className={cn(
        'relative flex min-h-[120px] w-[180px] flex-col items-center justify-center gap-2 rounded-lg border bg-destructive/10 p-4 shadow-sm',
        selected && 'ring-2 ring-destructive'
      )}
    >
      <div className='flex h-8 w-8 items-center justify-center rounded-md bg-destructive'>
        <Check className='h-5 w-5 text-destructive-foreground' />
      </div>
      <p className='text-sm font-medium'>{data.label || 'End'}</p>
      <Handle
        type='target'
        position={Position.Top}
        className='!border-2 !border-white !bg-slate-400 hover:!bg-primary'
      />
    </div>
  );
};
